// MouseTracker.tsx
import React, { useEffect, useRef } from 'react';

// Type definitions for User Agent Client Hints API
interface UADataValues {
	platform?: string;
	platformVersion?: string;
	architecture?: string;
	model?: string;
	uaFullVersion?: string;
}

interface NavigatorUAData {
	getHighEntropyValues(hints: string[]): Promise<UADataValues>;
}

declare global {
	interface Navigator {
		userAgentData?: NavigatorUAData;
	}
}

// Structure of a single position/interaction record
type Interaction = {
	x: number;
	y: number;
	t: number;
	type: 'move' | 'scroll';
};

// Options for the hook
type UseMouseTrackerOptions = {
	/** POST URL to receive payload */
	endpoint?: string;
	/** ms between batch sends (default: 200) */
	interval?: number;
	/** Custom session ID (if not provided, will generate/retrieve from cookie) */
	sessionId?: string;
	/** Cookie name for session ID (default: 'mouse_session_id') */
	sessionCookieName?: string;
	/** Cookie expiration in days (default: 1) */
	sessionCookieExpireDays?: number;
};

// Payload structure
type TrackerPayload = {
	sessionId: string;
	landingPageUrl: string;
	viewport: { width: number; height: number };
	deviceType: string;
	userAgent: string;
	interactions: Interaction[];
};

// Cookie utilities
const cookieUtils = {
	// Generate a unique session ID
	generateSessionId(): string {
		const timestamp = Date.now().toString(36);
		const randomPart = Math.random().toString(36).substring(2);
		return `ms_${timestamp}_${randomPart}`;
	},

	// Get cookie value by name
	getCookie(name: string): string | null {
		const value = `; ${document.cookie}`;
		const parts = value.split(`; ${name}=`);
		if (parts.length === 2) {
			const cookieValue = parts.pop()?.split(';').shift();
			return cookieValue || null;
		}
		return null;
	},

	// Set cookie with expiration
	setCookie(name: string, value: string, days: number = 1): void {
		const expires = new Date();
		expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
		const expiresString = expires.toUTCString();
		document.cookie = `${name}=${value}; expires=${expiresString}; path=/; SameSite=Lax`;
	},

	// Get or create session ID
	getOrCreateSessionId(cookieName: string = 'mouse_session_id', expireDays: number = 1): string {
		let sessionId = this.getCookie(cookieName);
		if (!sessionId) {
			sessionId = this.generateSessionId();
			this.setCookie(cookieName, sessionId, expireDays);
		}
		return sessionId;
	}
};

/**
 * Custom hook: captures mouse moves, scrolls, and touch moves
 * Sends batches offloaded to a Web Worker for JSON.stringify and network send
 * @param options.endpoint – API endpoint to receive payload
 * @param options.interval – milliseconds between each batch send
 */
function useMouseTracker({
	endpoint = '/api/mouse',
	interval = 200,
	sessionId,
	sessionCookieName = 'mouse_session_id',
	sessionCookieExpireDays = 1
}: UseMouseTrackerOptions = {}): void {
	const bufferRef = useRef<Interaction[]>([]);
	const timerRef = useRef<number>();
	const workerRef = useRef<Worker>();
	const blobUrlRef = useRef<string>();
	const lastMouseTimeRef = useRef<number>(0);
	const lastScrollTimeRef = useRef<number>(0);
	const staticDataRef = useRef<{
		sessionId: string;
		viewport: { width: number; height: number };
		userAgent: string;
		deviceType: string;
		landingPageUrl: string;
	}>();

	useEffect(() => {
		// Convert relative endpoint to absolute URL
		const absoluteEndpoint = endpoint.startsWith('http')
			? endpoint
			: new URL(endpoint, window.location.origin).toString();

		// Get or generate session ID
		const currentSessionId =
			sessionId ||
			cookieUtils.getOrCreateSessionId(sessionCookieName, sessionCookieExpireDays);

		// Function to get enhanced user agent data
		const getEnhancedUserAgent = (): string => {
			try {
				// Check if userAgentData is available (modern browsers)
				if (navigator.userAgentData && navigator.userAgentData.getHighEntropyValues) {
					// Use the async API but don't wait for it, just enhance on next batch
					navigator.userAgentData
						.getHighEntropyValues([
							'platform', // e.g. "Windows"
							'platformVersion', // e.g. "13.0.22621.1702" on Win11
							'architecture', // e.g. "x86"
							'model', // device model, if available
							'uaFullVersion' // full browser version string
						])
						.then((ua) => {
							// Update static data with enhanced user agent when available
							if (staticDataRef.current) {
								const parts = [];
								if (ua.platform) parts.push(`Platform: ${ua.platform}`);
								if (ua.platformVersion)
									parts.push(`PlatformVersion: ${ua.platformVersion}`);
								if (ua.architecture) parts.push(`Architecture: ${ua.architecture}`);
								if (ua.model) parts.push(`Model: ${ua.model}`);
								if (ua.uaFullVersion)
									parts.push(`FullVersion: ${ua.uaFullVersion}`);

								staticDataRef.current.userAgent = `${navigator.userAgent} | Enhanced: ${parts.join(', ')}`;
							}
						})
						.catch((error) => {
							console.warn('Failed to get enhanced user agent data:', error);
						});
				}
			} catch (error) {
				console.warn('userAgentData API not available:', error);
			}

			// Return standard userAgent immediately
			return navigator.userAgent;
		};

		// Cache static data once
		const viewport = { width: window.innerWidth, height: window.innerHeight };
		const userAgent = getEnhancedUserAgent();
		const deviceType = /Mobi|Android|iPhone|iPad/.test(navigator.userAgent)
			? 'mobile'
			: 'desktop';
		staticDataRef.current = {
			sessionId: currentSessionId,
			viewport,
			userAgent,
			deviceType,
			landingPageUrl: window.location.href
		};

		// Initialize Web Worker with optimized code
		const workerCode = `
      self.onmessage = (e) => {
        const { endpoint, staticData, interactions } = e.data;
        try {
          const payload = JSON.stringify({
            ...staticData,
            interactions
          });
          fetch(endpoint, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json'
            },
            keepalive: true,
            body: payload
          });
        } catch (err) {
          // silently ignore
        }
      };
    `;
		const blob = new Blob([workerCode], { type: 'application/javascript' });
		const blobUrl = URL.createObjectURL(blob);
		blobUrlRef.current = blobUrl;
		const worker = new Worker(blobUrl);
		workerRef.current = worker;

		// Throttled event handlers with performance optimizations
		const onMouseMove = (e: MouseEvent): void => {
			const now = performance.now();
			// Throttle to max 60fps
			if (now - lastMouseTimeRef.current < 16) return;
			lastMouseTimeRef.current = now;

			bufferRef.current.push({
				x: e.clientX,
				y: e.clientY,
				t: Date.now(), // Use Date.now() for absolute timestamps
				type: 'move'
			});
		};

		const onScroll = (): void => {
			const now = performance.now();
			// Throttle scroll events to 30fps
			if (now - lastScrollTimeRef.current < 33) return;
			lastScrollTimeRef.current = now;

			bufferRef.current.push({
				x: window.scrollX,
				y: window.scrollY,
				t: Date.now(), // Use Date.now() for absolute timestamps
				type: 'scroll'
			});
		};

		const onTouchMove = (e: TouchEvent): void => {
			const now = performance.now();
			if (now - lastMouseTimeRef.current < 16) return;
			lastMouseTimeRef.current = now;

			const touch = e.touches[0];
			if (!touch) return;

			bufferRef.current.push({
				x: touch.clientX,
				y: touch.clientY,
				t: Date.now(), // Use Date.now() for absolute timestamps
				type: 'move'
			});
		};

		// Use passive listeners for better performance
		window.addEventListener('mousemove', onMouseMove, { passive: true });
		window.addEventListener('scroll', onScroll, { passive: true });
		window.addEventListener('touchmove', onTouchMove, { passive: true });

		// Optimized batch sending
		const sendBatch = (interactions: Interaction[]): void => {
			if (!staticDataRef.current || !workerRef.current) return;
			workerRef.current.postMessage({
				endpoint: absoluteEndpoint,
				staticData: staticDataRef.current,
				interactions
			});
		};

		// Periodic flush with requestIdleCallback fallback
		const flushBuffer = () => {
			const buf = bufferRef.current.splice(0);
			if (buf.length) sendBatch(buf);
		};

		if (window.requestIdleCallback) {
			const scheduleFlush = () => {
				window.requestIdleCallback(() => {
					flushBuffer();
					timerRef.current = window.setTimeout(scheduleFlush, interval);
				});
			};
			scheduleFlush();
		} else {
			timerRef.current = window.setInterval(flushBuffer, interval);
		}

		// Cleanup on unmount
		return () => {
			window.removeEventListener('mousemove', onMouseMove);
			window.removeEventListener('scroll', onScroll);
			window.removeEventListener('touchmove', onTouchMove);
			if (timerRef.current) {
				clearTimeout(timerRef.current);
				clearInterval(timerRef.current);
			}
			const remaining = bufferRef.current;
			if (remaining.length) sendBatch(remaining);
			worker.terminate();
			if (blobUrlRef.current) {
				URL.revokeObjectURL(blobUrlRef.current);
			}
		};
	}, [endpoint, interval, sessionId, sessionCookieName, sessionCookieExpireDays]);
}

interface MouseTrackerProps {
	/** API endpoint to receive batches */
	endpoint?: string;
	/** ms between batch sends */
	interval?: number;
	/** Custom session ID (if not provided, will generate/retrieve from cookie) */
	sessionId?: string;
	/** Cookie name for session ID (default: 'mouse_session_id') */
	sessionCookieName?: string;
	/** Cookie expiration in days (default: 1) */
	sessionCookieExpireDays?: number;
}

/**
 * MouseTracker component: invisible; starts tracking interactions on mount
 */
const MouseTracker: React.FC<MouseTrackerProps> = ({
	endpoint = '/api/mouse',
	interval = 200,
	sessionId,
	sessionCookieName,
	sessionCookieExpireDays
}) => {
	useMouseTracker({
		endpoint,
		interval,
		sessionId,
		sessionCookieName,
		sessionCookieExpireDays
	});
	return null;
};

export default MouseTracker;
export { useMouseTracker, cookieUtils };
export type { UseMouseTrackerOptions, TrackerPayload, Interaction };
